import { createSlice, createSelector } from '@reduxjs/toolkit';
import { logout } from "../common/reducers/initContext";
import { dataHookFactory } from '../common/reducers/utilities';
import { ensureArray } from '../common/utils/arrayUtilities';

const compareResults = (r1, r2) => r1.id === r2.id;

const resultSlice = createSlice({
    name: 'results',
    initialState: {
        data: []
    },
    reducers: {
        set: (state, action) => {
            state.data = action.payload;
        },
        addOrUpdate: (state, action) => {
            for (const result of ensureArray(action.payload)) {
                let idx = state.data.findIndex(r => compareResults(r, result));
                if (idx >= 0) {
                    state.data.splice(idx, 1, result);
                } else {
                    state.data.push(result);
                }
            }
        },
        remove: (state, action) => {
            for (const r of ensureArray(action.payload)) {
                let idx = state.data.findIndex(r2 => compareResults(r, r2));
                if (idx >= 0) {
                    state.data.splice(idx, 1);
                }
            }
        }
    },
    extraReducers: builder => {
        builder.addCase(logout, () => resultSlice.getInitialState());
    }
})

// #region network operations
const fetchResults = network => network.post('/api/v1/results', {}, resultSlice.actions.set);
export const createResult = (network, result) => network.post('/api/v1/result', result, resultSlice.actions.addOrUpdate);
export const removeResult = (network, result) => network.delete('/api/v1/result/' + encodeURIComponent(result.id), resultSlice.actions.remove);
// #endregion

// #region permission checks
export const canAddResult = (credentials, result) => credentials.access.hasFull('administrator');
export const canRemoveResult = (credentials, result) => credentials.access.hasFull('administrator');
// #endregion

// #region selectors
const selectAllResults = state => state.results.data;
const selectResultBySiteLevelType = createSelector([selectAllResults, (results, site) => site, (results, site, level) => level, (results, site, level, type) => type], (results, site, level, type) => {
    return results.filter(r => (!r.site || r.site === site) && (!r.level || r.level === level) && (!r.type || r.type === type));
});
const selectResults = (site, level, type) => state => selectResultBySiteLevelType(state, site, level, type);

// #region listeners
const listen = {
    resultCreated: (result, { dispatch }) => dispatch(resultSlice.actions.addOrUpdate(result)),
    resultRemoved: (result, { dispatch }) => dispatch(resultSlice.actions.remove(result))
}
// #endregion
export const [useAllResults] = dataHookFactory(selectAllResults, { run: fetchResults, listen });
export const [useResults] = dataHookFactory(selectResults, (site, level, type) => ({ run: fetchResults, listen }));

const reducerDetails = { name: resultSlice.name, reducer: resultSlice.reducer };
export default reducerDetails;